import { createApi } from "@reduxjs/toolkit/query/react";

import { REQUEST_METHODS } from "@/constants/global-constants";
import {
  IWareHouse,
  IWareHouseAssignHistory,
  IWareHouseDetailItem,
  IWareHouseProject,
} from "@/interfaces/ware-house.interface";
import { BaseWithAuth, getWorkspace } from "@/utils/helpers";

export const wareHousingApi = createApi({
  reducerPath: "wareHousingApi",
  baseQuery: BaseWithAuth(),
  tagTypes: [
    "warehousing",
    "warehousingDetailItems",
    "warehousingDetailProjects",
    "warehousingDetailsAssignHistory",
  ],
  endpoints: (builder) => ({
    //Get WareHouse List
    getWareHouses: builder.query<
      GeneralResponse<IWareHouse[]>,
      { filters: string }
    >({
      query: ({ filters }) =>
        `workspace/${getWorkspace()}/warehouse${filters ? `?${filters}` : ""}`,
      providesTags: ["warehousing"],
    }),
    // Change the status of the warehouse
    changeWareHouseStatus: builder.mutation<
      object,
      { warehouseId: string; data: object }
    >({
      query: ({ warehouseId, data }) => ({
        url: `workspace/${getWorkspace()}/warehouse/${warehouseId}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["warehousing"],
    }),
    //Create WareHouse
    createWareHouse: builder.mutation<object, object>({
      query: (data) => ({
        url: `workspace/${getWorkspace()}/warehouse`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["warehousing"],
    }),

    //Get Individual WareHouse Items
    getWareHouseItems: builder.query<
      GeneralResponse<IWareHouseDetailItem[] | IWareHouseDetailItem>,
      {
        warehouseId: string;
        filters?: string;
        warehouseItemId?: string;
      }
    >({
      query: ({ filters, warehouseId, warehouseItemId }) =>
        `workspace/${getWorkspace()}/warehouse/${warehouseId}/items${warehouseItemId ? `/${warehouseItemId}` : ""}${filters ? `?${filters}` : ""}`,
      providesTags: ["warehousingDetailItems"],
    }),
    //Alter WareHouse Detail Item
    alterWareHouseItem: builder.mutation<
      object,
      {
        warehouseId: string;
        warehouseItemId?: string;
        isUpdateQuantity?: boolean;
        method: REQUEST_METHODS;
        data?: object;
      }
    >({
      query: ({
        data,
        method,
        warehouseId,
        warehouseItemId,
        isUpdateQuantity,
      }) => ({
        url: `workspace/${getWorkspace()}/warehouse/${warehouseId}/items${warehouseItemId ? `/${warehouseItemId}` : ""}${isUpdateQuantity ? "/quantity" : ""}`,
        method,
        body: data,
      }),
      invalidatesTags: ["warehousingDetailItems"],
    }),
    //Assign Warehouse Detail Project
    alterAssignProject: builder.mutation<
      object,
      {
        warehouseId: string;
        projectId?: string;
        method: REQUEST_METHODS;
        data: object;
      }
    >({
      query: ({ warehouseId, projectId, method, data }) => ({
        url: `workspace/${getWorkspace()}/warehouse/${warehouseId}/projects/${projectId ? projectId : ""}`,
        method,
        body: data,
      }),
      invalidatesTags: ["warehousingDetailItems", "warehousingDetailProjects"],
    }),
    //Get WareHouse Detail Projects
    getWareHouseDetailProjects: builder.query<
      GeneralResponse<IWareHouseProject[] | IWareHouseProject>,
      { warehouseId: string; filters?: string; projectId?: string }
    >({
      query: ({ warehouseId, filters, projectId }) =>
        `workspace/${getWorkspace()}/warehouse/${warehouseId}/projects${projectId ? `/${projectId}` : ""}${filters ? `?${filters}` : ""}`,
      providesTags: ["warehousingDetailProjects"],
    }),
    //Get WareHouse Detail Assign History
    getWareHouseDetailAssignHistory: builder.query<
      GeneralResponse<IWareHouseAssignHistory[]>,
      { warehouseId: string; filters?: string }
    >({
      query: ({ warehouseId, filters }) =>
        `workspace/${getWorkspace()}/warehouse/${warehouseId}/history${filters ? `?${filters}` : ""}`,
      providesTags: ["warehousingDetailsAssignHistory"],
    }),
    //Return the item
    returnItem: builder.mutation<
      object,
      {
        warehouseId: string;
        warehouseItemId: string;
        data: object;
      }
    >({
      query: ({ warehouseId, warehouseItemId, data }) => ({
        url: `workspace/${getWorkspace()}/warehouse/${warehouseId}/projects/${warehouseItemId}/return`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["warehousingDetailItems", "warehousingDetailProjects"],
    }),
    //Change the status of the project
    changeProjectStatus: builder.mutation<
      object,
      {
        warehouseId: string;
        projectId: string;
        data: object;
      }
    >({
      query: ({ warehouseId, projectId, data }) => ({
        url: `workspace/${getWorkspace()}/warehouse/${warehouseId}/projects/${projectId}/status`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["warehousingDetailProjects"],
    }),
  }),
});

export const {
  useAlterWareHouseItemMutation,
  useChangeWareHouseStatusMutation,
  useGetWareHousesQuery,
  useCreateWareHouseMutation,
  useAlterAssignProjectMutation,
  useGetWareHouseItemsQuery,
  useGetWareHouseDetailProjectsQuery,
  useGetWareHouseDetailAssignHistoryQuery,
  useReturnItemMutation,
  useChangeProjectStatusMutation,
} = wareHousingApi;
